import { getOrderBookDetailAPI, getOrderbookDeliveryPdfAPI } from "api/order-book";
import { useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { SimpleModal } from "./SimpleModal";
import { Descriptions, Divider, Flex, Space, Table } from "antd";
import { DataGrid, FillButton } from "components/elements";
import { TitleLayout } from "components/layouts";
import {BasePdfContainer, OrderBookPdfModal} from 'components/containers';
import { getAmountWithComma, getFormatDate } from "utils/utils";
import { orderStatusOptions, payMethodOptions } from "data/options";
import { BaseContainer } from "./BaseContainer";
import { OrderItem } from "components/biz";

export function OrderBookDetailModal({onOpen, onClose, orderIdx}) {
  const [orderInfo, setOrderInfo] = useState(null)
  const [orderBookList, setOrderBookList] = useState([])
  const [orderEtcList, setOrderEtcList] = useState([])
  const [orderBookViewList, setOrderBookViewList] = useState([])
  const [orderEtcViewList, setOrderEtcViewList] = useState([])
  const [schoolInfo, setSchoolInfo] = useState({})
  const [pdfList,setPdfList] = useState([]);
  const contentRef = useRef(null);
  const handlePrint = useReactToPrint({contentRef});

  const fnConversionView = (list) => {
    const map = new Map()
    list?.forEach(item => { // 같은 교재 합치기
      item?.orderDetailGroupBook?.forEach(it => {
        const key = `${it.book_cate1_code}-${it.product_class_sort}-${it.product_cd}`
        if (!map?.has(key)) {
          map.set(key, {...it})
        } else {
          map.get(key).cnt += it.cnt
        }
      })
    })

    const groupBy = [...map?.values()]?.reduce((acc, item) => { // 클래스(분류)별 구분
      const { product_class_idx: idx, product_class_sort: sort } = item
    
      if (!acc[`${idx}-${sort}`]) {
        acc[`${idx}-${sort}`] = []
      }
    
      acc[`${idx}-${sort}`].push(item)
      return acc
    }, {})

    const orderBy = Object.keys(groupBy)?.sort((a, b) => // 클래스 정렬
      Number(a?.split('-')?.[1]) - Number(b?.split('-')?.[1])
    ).reduce((acc, key) => { // 정렬 순으로 객체 재생성
      acc[key] = groupBy[key]
      return acc
    }, {})

    Object.keys(orderBy).forEach(key => { // 교재 정렬
      orderBy[key].sort((a, b) => a.sort_no - b.sort_no)
    })

    const result = Object.values(orderBy) // (표현하기 쉽게) 배열로 변환
    return result
  }

  const fnGetDetail = async () => {
    try {
      const { data } = await getOrderBookDetailAPI(orderIdx)
      const { data_info, order_addr_info, data_list } = data
      const bookList = data_list?.filter(it => it.member_idx)
      const etcList = data_list?.filter(it => !it.member_idx)

      const etc_cnt = data_list?.filter(item => item?.order_type === "ETC")?.length;
      setOrderInfo({...data_info, ORDER_ADDR_INFO: order_addr_info, ORDER_ETC_CNT: etc_cnt})
      setOrderBookList(bookList)
      setOrderEtcList(etcList)
      setSchoolInfo(order_addr_info.find(it => it.GUBUN === 2))

      // viewList 데이터 변환 과정
      const orderBookView = fnConversionView(bookList)
      setOrderBookViewList(orderBookView)
      const orderEtcView = fnConversionView(etcList)
      setOrderEtcViewList(orderEtcView)


      fnHandlePrint();
    } catch (err) {
      console.error(err)
    }
  }

  //pdf 배송용 출력
  const fnHandlePrint = async () => {
    try {
      const {data} = await getOrderbookDeliveryPdfAPI([orderIdx])
      setPdfList(data.data_list);
    }catch(err){
      console.error(err);
    }
  }


  useEffect(() => {
    if (onOpen) fnGetDetail()
  }, [onOpen])

  const orderAddrInfoColumns = [
    { title: '구분', align: 'center', width:'20%', dataIndex: 'NAME' },
    { title: '우편번호', align: 'center', width:'10%', dataIndex: 'DELIVERY_ZIP_CODE' },
    { title: '배송주소', align: 'center', width:'50%', dataIndex: 'DELIVERY_ADDR_DETAIL',
      render: (text, record) => `${record.DELIVERY_ADDR} ${text || ''}`
    },
    { title: '연락처', align: 'center', width:'20%', dataIndex: 'CELL_PHONE',
      render: (text, record) => `${text} / ${record.HOME_PHONE || '-'}`
    },
  ]

  const orderInfoItems = [
    { key: '1', label: '학습기간', children: orderInfo?.STUDY_DATE?.substring(0, 4)+'-'+orderInfo?.STUDY_DATE?.substring(4) },
    { key: '2', label: '주문일', children: orderInfo?.ORDER_DATE },
    { key: '3', label: '결제방식', children: payMethodOptions.find(it => it.value === orderInfo?.PAY_TYPE)?.label},
    { key: '4', label: '주문상태', children: orderStatusOptions.find(it => it.value === orderInfo?.ORDER_STATUS)?.label}
  ]

  const payInfoItems = [
    { key: '1', label: '기본교재', 
      children: 
      <Space direction={'vertical'} size={0}>
        <div>{getAmountWithComma(orderInfo?.ORDER_BOOK_PRICE)}원 ({orderInfo?.ORDER_BOOK_CNT}권)</div>
        {orderInfo?.REFUND_BOOK_CNT !== 0 && <div className='text-red'>-{getAmountWithComma(orderInfo?.REFUND_BOOK_PRICE)}원 (-{orderInfo?.REFUND_BOOK_CNT}권)</div>}
      </Space> 
    },
    { key: '2', label: '검사지', children: `${getAmountWithComma(orderInfo?.ORDER_SPTP_PRICE)}원 (${orderInfo?.ORDER_SPTP_CNT}권)` },    
    { key: '3', label: '기타/물품', 
      children: 
        <Space direction={'vertical'} size={0}>
          <div>{getAmountWithComma(orderInfo?.ORDER_ETC_PRICE)}원 ({orderInfo?.ORDER_STATUS === '70' ? 0 : orderEtcList?.length}가지)</div>
          {orderInfo?.REFUND_ETC_CNT !== 0 && <div className='text-red'>-{getAmountWithComma(orderInfo?.REFUND_ETC_PRICE)}원 (-{orderInfo?.REFUND_ETC_CNT}가지)</div>}
        </Space>
    }, 
    { key: '4', label: '배송비', children: `${getAmountWithComma(orderInfo?.DELIVERY_PRICE)}원` },  
    { key: '5', label: '사용 적립금', children: `${getAmountWithComma(orderInfo?.USE_POINT)}원` },
    { key: '6', label: '결제금액', children: `${getAmountWithComma(orderInfo?.TOTAL_PRICE - orderInfo?.USE_POINT + orderInfo?.DELIVERY_PRICE)}원`},
    { key: '7', label: '환불', children: `${orderInfo?.REFUND_PRICE ? '-' : ''}${getAmountWithComma(orderInfo?.REFUND_PRICE)}원`}
  ]

  const orderBookColumns =  [
    { title: '번호', align: 'center', width: '10%',
      render: (text, record, index) => index+1
    },
    { title: '회원명', align: 'center', width: '15%', dataIndex: 'member_name' },
    { title: '나이', align: 'center', width: '5%', dataIndex: 'member_age',
      render: (text) => `${text}세`
    },
    { title: '교육과정', align: 'center', width: '15%', dataIndex: 'book_cate1' },
    { title: <Flex justify={'center'}>교재내역</Flex>, width: '30%', dataIndex: 'orderDetailGroupBook',
      render: (text) => 
        <Space direction={'vertical'} size={0}>
          {text?.map((it,idx) => 
            <span className={it.order_group_detail_status === '94' && 'text-delete'}>{it.product_name} ({getAmountWithComma(it.sale_price)}원)</span>
          )}
        </Space>
    },
    { title: '권수', align: 'center', width: '10%', dataIndex: 'orderDetailGroupBook',
      render: (text) => `${text.length}권`
    },
    { title: '합계', align: 'center', width: '15%', dataIndex: 'total_price',
      render: (text) => `${getAmountWithComma(text)}원`
    }
  ]
  
  const orderEtcColumns =  [
    { title: '상품명', align: 'center', width: '65%', dataIndex: 'orderDetailGroupBook',
      render: (text) => <span className={text?.[0]?.order_group_detail_status === '94' && 'text-delete'}>{text?.[0]?.product_name}</span>
    },
    { title: '수량', align: 'center', width: '10%', dataIndex: 'orderDetailGroupBook',
      render: (text) => <span className={text?.[0]?.order_group_detail_status === '94' && 'text-delete'}>{text?.[0]?.cnt}</span>
    },
    { title: '합계', align: 'center', width: '25%', dataIndex: 'total_price',
      render: (text) => `${getAmountWithComma(text)}원`
    }
  ]

  return (
    <>
    <SimpleModal
      open={onOpen} 
      title={'교재주문 상세보기'}
      width={'60%'}
      onClose={onClose}
      footer={[
        <Flex justify='center' gap={'small'}>
          <FillButton 
            key={'close'} 
            label='닫기' 
            color={'var(--color-gray03)'} 
            backgroundColor={'var(--color-gray04)'} 
            onClick={onClose} 
          />
          <FillButton label='배송용 페이지 출력' backgroundColor={'var(--color-gray03)'} onClick={handlePrint} />
        </Flex>
      ]}
    >
      <>
      <Flex justify={'space-between'}>
        <TitleLayout>주문 상세보기</TitleLayout>
        <Space size={3}>
          {`[주문번호] ${orderIdx} |`}
          <Space size={3}>
            [주문 교육원] 
            <span className='text-blue'> 
              {`${schoolInfo?.NAME} (${schoolInfo?.ID})`}
            </span> 
          </Space>
        </Space>
      </Flex>
      <Divider className="divider-small" />

      <DataGrid
        rowKey={'key'}
        bordered
        columns={orderAddrInfoColumns}
        data={orderInfo?.ORDER_ADDR_INFO}
      />
      <Divider className="divider" />

      <Descriptions 
        bordered 
        layout="vertical" 
        column={4} 
        labelStyle={{ textAlign: 'center' }} 
        contentStyle={{ textAlign: 'center', verticalAlign: 'middle' }}
        items={orderInfoItems}
      />
      <Divider className='divider-small' />

      <Descriptions 
        bordered 
        column={7} 
        layout="vertical" 
        labelStyle={{ textAlign: 'center' }} 
        contentStyle={{ textAlign: 'center', verticalAlign: 'middle' }}
        items={payInfoItems}
      />
      <Divider className='divider' />

      <TitleLayout>교재 주문내역</TitleLayout>
      <Divider className="divider-small" />

      <DataGrid 
        rowKey={'key'}
        bordered
        columns={orderBookColumns}
        data={orderBookList}
        summary={(pageData) => {
          let cnt = 0
          let price = 0
          pageData?.map(it => {
            cnt += it.orderDetailGroupBook.length
            price += it.total_price
          })

          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7}>
                <Flex justify={'flex-end'} gap={10}>
                  <span className="text-red">{cnt-orderInfo?.REFUND_BOOK_CNT}권</span>
                  <span className="text-red">{getAmountWithComma(price-orderInfo?.REFUND_BOOK_PRICE)}원</span>
                </Flex>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )
        }}
      />
      <Divider className='divider' />

      <TitleLayout>기타/물품 주문내역</TitleLayout>
      <Divider className="divider-small" />

      <DataGrid 
        rowKey={'key'}
        bordered
        columns={orderEtcColumns}
        data={orderEtcList}
        summary={(pageData) => {
          let price = 0
          pageData?.map(it => {
            price += it?.total_price
          })

          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <Flex justify={'flex-end'} gap={10}>
                  <span className="text-red">{orderInfo?.ORDER_ETC_CNT}가지</span>
                  <span className="text-red">{getAmountWithComma(price)}원</span>
                </Flex>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )
        }}
      />
      <Divider className="divider" />

      <BaseContainer borderColor={'var(--color-gray10)'}>
        <Flex justify={'space-between'}>
          <Space direction={'vertical'} size={0}>
            <div>{schoolInfo?.NAME} / {schoolInfo?.HOME_PHONE || '-'} / {schoolInfo?.CELL_PHONE}</div>
            <div>{schoolInfo?.DELIVERY_ADDR} {schoolInfo?.DELIVERY_ADDR_DETAIL} ({schoolInfo?.DELIVERY_ZIP_CODE})</div>
          </Space>
          주문일 : {orderInfo?.ORDER_DATE}
        </Flex>
        <Divider className="divider" />

        <TitleLayout>교재</TitleLayout>
        <Divider className="divider-small" />
        <Flex gap='small' vertical>
         {orderBookViewList?.map(item => 
            <Space>
              {item?.map(it => 
                <OrderItem title={it.product_name} content={`${it.cnt}권`} />
              )}
            </Space>
          )}
        </Flex>
        <Divider className="divider" />

        <TitleLayout>기타/물품</TitleLayout>
        <Divider className="divider-small" />

        <Flex vertical={true} gap={'middle'}>
          {orderEtcViewList?.map(item => 
            <Space>
              {item?.map(it => 
                <OrderItem title={it.product_name} content={`${it.cnt}권`} />
              )}
            </Space>
          )}
        </Flex>
      </BaseContainer>
    </>
    </SimpleModal>
    
    <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
      <div ref={contentRef}>
          {pdfList?.map((it,idx) => 
          <>
            <BasePdfContainer 
              key={`book-pdf-modal-${orderIdx}`} 
              addr_info={{...it.addr_info[0], ORDER_IDX : `${orderIdx}_${it.page.split('/')[0]}`}} >
                <Flex justify={'space-between'}>
                <Space direction={'vertical'} size={0}>
                  <div>{it.addr_info[0]?.SCHOOL_NAME} / {it.addr_info[0]?.SCHOOL_CELL_PHONE} / {it.addr_info[0]?.SCHOOL_HOME_PHONE}</div>
                  <div>{it.addr_info[0]?.SCHOOL_DELIVERY_ADDR} {it.addr_info[0]?.SCHOOL_DELIVERY_ADDR_DETAIL} ({it.addr_info[0]?.SCHOOL_DELIVERY_ZIP_CODE})</div>
                </Space>
                <div>주문일 : {it.addr_info[0]?.ORDER_DATE}</div>
              </Flex>
              <TitleLayout>교재</TitleLayout>
              <Divider className="divider-small"/>
              <Flex gap='small' vertical>
                {
                  it.book_list?.reduce((acc,cur)=>{
                    if(!acc.includes(cur.PRODUCT_CLASS_IDX) && cur.PRODUCT_BOOK_TYPE ===1) {
                      acc.push(cur.PRODUCT_CLASS_IDX)
                    }
                    return acc
                  },[]).map(elem => {
                      const items = it.book_list?.filter(j =>j.PRODUCT_CLASS_IDX === elem )
                      return <Flex gap='small'>
                      {items.map(inItem => <OrderItem title={inItem.PRODUCT_NAME} content={`${inItem.CNT}권`} /> )}
                      </Flex>
                      
                  })
              
                }
              </Flex>
              <TitleLayout>기타/물품</TitleLayout>
              <Divider className="divider-small"/>

              <Flex vertical={true} gap={'middle'}>
                <Space>
                  {it.book_list.filter(i => i.PRODUCT_BOOK_TYPE == 2).map(item =>
                    <OrderItem title={item.PRODUCT_NAME} content={`${item.CNT}건`}/>
                  )}
                </Space>
              </Flex>
              
              <Flex justify="flex-end">
                (총 : {it?.book_list_cnt} 권)
              </Flex>
            </BasePdfContainer>
            <div style={{width:'100%', height:'18px', textAlign:'center', borderBottom:'1px solid gray'}}>{it?.page}</div>
          </>
          )}  
      </div>
    </div>
    </>
  )
}